import { Drawer, message } from 'antd';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useCallback, useMemo, useState } from 'react';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';
import { useWindowSize } from 'react-use';
import Link from 'next/link';
import { useUserProvider } from '@/context/UserProvider';
import { fetchInstagramAnonymouslyTimesData } from '@/lib/service/anonymously';
import { LimitAlert } from '../../FeeLimitAlert';
import router from 'next/router';

const StoriesLinkContent = ({
  isDetail = false,
  links,
  referToList,
}: {
  isDetail?: boolean;
  links?: string[];
  referToList?: string[];
}) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLinks, setIsLinks] = useState(false);
  const { width } = useWindowSize();
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [showLimit, setShowLimit] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleItemClick = useCallback(
    async (list: string[] | undefined, isLinksType: boolean) => {
      if (!isDetail || !list?.length || loading) return;

      if (!isLogin) {
        onOpenLoginModal(true);
        onChangedLoginView(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetchInstagramAnonymouslyTimesData(null);
        if (res?.code === 0) {
          if (res?.data?.un_limit) {
            setIsLinks(isLinksType);
            setDrawerOpen(true);
          } else {
            setShowLimit(true);
          }
        } else if (res?.code === 40001) {
          onOpenLoginModal(true);
          onChangedLoginView(false);
        } else {
          message.error(res?.message);
        }
      } finally {
        setLoading(false);
      }
    },
    [isDetail, isLogin, loading, onChangedLoginView, onOpenLoginModal]
  );

  const getTitleContent = useMemo(() => {
    return <div className={styles.linkTitle}>{isLinks ? t('Mentioned Links') : t('Mentioned Users')}</div>;
  }, [isLinks]);

  const getInfoContent = useMemo(() => {
    return (
      <div style={{ width: '100%', marginBottom: '20px' }}>
        <p className={styles.linkSubTitle}>
          {isLinks
            ? `${links?.length ?? 0} Links Mentioned in this Story`
            : `${referToList?.length ?? 0} Person Mentioned in this Story`}
        </p>

        <div className={styles.listContent}>
          {isLinks
            ? links?.map((item, index) => {
                return (
                  <Link key={index} href={item ?? '#'} target="_blank" className={styles.link}>
                    {item}
                  </Link>
                );
              })
            : referToList?.map((item, index) => {
                return (
                  <Link key={index} href={`https://www.instagram.com/${item}/`} target="_blank" className={styles.link}>
                    @{item}
                  </Link>
                );
              })}
        </div>
      </div>
    );
  }, [isLinks, links, referToList]);

  return (
    <>
      <div className={styles.groundContent}>
        {[
          { key: 'refer', list: referToList, isLinksType: false, icon: '/images/landingpage/refer_icon.webp' },
          { key: 'links', list: links, isLinksType: true, icon: '/images/landingpage/links_icon.webp' },
        ].map(({ key, list, isLinksType, icon }) => {
          const count = list?.length ?? 0;

          return (
            <button
              key={key}
              className={styles.itemContainer}
              style={{
                background: count > 0 ? '#725CFF' : '#454545',
                cursor: isDetail && count > 0 ? 'pointer' : 'not-allowed',
              }}
              onClick={() => handleItemClick(list, isLinksType)}
            >
              <Image src={icon} alt={''} width={16} height={16} />
              <span>{count}</span>
            </button>
          );
        })}
      </div>

      {width <= 786 && (
        <Drawer
          title={getTitleContent}
          placement={'bottom'}
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          rootStyle={{ zIndex: 99999 }}
        >
          {getInfoContent}
        </Drawer>
      )}

      {width > 786 && (
        <CommonSimpleModal
          zIndex={99999}
          open={drawerOpen}
          title={getTitleContent}
          desc={getInfoContent}
          btnTitle={t('OK')}
          closeAction={(isOk) => {
            setDrawerOpen(false);
          }}
        />
      )}

      <LimitAlert
        open={showLimit}
        closeAction={(isOk) => {
          setShowLimit(false);

          if (isOk) {
            router.push('/download-from-instagram/#price');
          }
        }}
      />
    </>
  );
};

export default StoriesLinkContent;
